import type { BindingSpec, BindingPrecedence, NodeBindings } from '@features/flows/utils/types';
import { deepMerge } from '@features/flows/utils/deep-merge';

export const DEFAULT_PRECEDENCE: BindingPrecedence = 'pin>attr';

export function isPinned(spec?: BindingSpec): boolean {
  return !!spec?.pin && spec.exposable !== false;
}

export function ensureBinding(spec: BindingSpec | undefined, field: string): BindingSpec {
  return {
    attr: spec?.attr ?? field,
    pin: spec?.pin,
    precedence: spec?.precedence ?? DEFAULT_PRECEDENCE,
    combine: spec?.combine ?? 'replace',
    sensitive: spec?.sensitive ?? false,
    exposable: spec?.exposable ?? true,
  };
}

/** Resolve a single field value according to binding precedence+combine. */
export function resolveBindingValue<T = unknown>(
  spec: BindingSpec | undefined,
  fromAttr: T | undefined,
  fromPin: T | undefined,
): T | undefined {
  const b = ensureBinding(spec ?? {}, spec?.attr ?? '');
  const isDef = (x: any) => x !== undefined; // null counts as defined
  const choose = (first: 'pin' | 'attr') => (first === 'pin' ? (isDef(fromPin) ? fromPin : fromAttr) : (isDef(fromAttr) ? fromAttr : fromPin));
  switch (b.precedence) {
    case 'pinOnly': return fromPin;
    case 'attrOnly': return fromAttr;
    case 'attr>pin': {
      if (b.combine === 'merge' && isPlainObject(fromAttr) && isPlainObject(fromPin)) {
        return deepMerge(fromPin as any, fromAttr as any) as T;
      }
      return choose('attr') as T;
    }
    case 'pin>attr':
    default: {
      if (b.combine === 'merge' && isPlainObject(fromAttr) && isPlainObject(fromPin)) {
        return deepMerge(fromAttr as any, fromPin as any) as T;
      }
      return choose('pin') as T;
    }
  }
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

export type { BindingSpec, NodeBindings };

